import React, { useCallback, useEffect, useMemo } from "react";
import { Icon } from "antd";
import { connect, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as R from "ramda";

import * as gtag from "../../lib/gtag";
import OpportunityCard from "../OpportunityCard";
import Filters from "../transforms/Filters";
import Pagination from "../transforms/Pagination";
import {
  FETCH_OPPORTUNITIES,
  fetchNotifications
} from "../../store/actions/opportunities";
import {
  byTeam,
  assocNotifications
} from "../../store/selectors/opportunities";
import { PAGE_SIZE } from "../../constants/opportunity";
import { isLoading } from "../../utils/actions";

import SkeletonOpportunities from "./SkeletonOpportunities";

import styled from "styled-components/macro";

const curriedIsLoading = R.curry(isLoading)(FETCH_OPPORTUNITIES);

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  grid-gap: 24px;
  @media screen and (-ms-high-contrast: active),
    screen and (-ms-high-contrast: none) {
    /* IE10+ specific styles go here */
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
    > div {
      width: 32%;
      margin-bottom: 18px;
    }
  }
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 550px;
  font-size: 1.5em;
  i {
    font-size: 60px;
    margin-bottom: 20px;
    opacity: 0.3;
  }
`;

function Opportunities({ company, opportunities, loading, idUser, error }) {
  const { t } = useTranslation(["opportunities", "global"]);
  const dispatch = useDispatch();

  useEffect(() => {
    gtag.event({
      category: "Vacancies",
      action: "Vacancies.List",
      label: company.id
    });
  }, [company.id]);

  useEffect(() => {
    const notificationsInterval = setInterval(
      () => dispatch(fetchNotifications()),
      10000
    );
    return () => {
      clearInterval(notificationsInterval);
    };
  }, [company.id, dispatch]);

  useEffect(() => {
    return () => dispatch(FETCH_OPPORTUNITIES.clearStatus());
  }, [dispatch]);

  const opportunitiesLoading = useMemo(
    () => loading || opportunities === null,
    [loading, opportunities]
  );

  const handleRadio = useCallback(
    option => (value = []) => {
      if (option === "all") return value;

      if (option === "mine") return R.filter(R.propEq("owner", idUser), value);

      if (option === "others")
        return R.reject(R.propEq("owner", idUser), value);
    },
    [idUser]
  );

  return (
    <div data-test="opportunity-list">
      <Filters
        data={opportunities || []}
        searchProp="title"
        radioTitle={t("opportunities:byCreator")}
        radioSelector={handleRadio}
        defaultRadioValue="all"
        radioOptions={[
          {
            value: "all",
            label: t("opportunities:all")
          },
          {
            value: "mine",
            label: t("opportunities:createdByMe")
          },
          {
            value: "others",
            label: t("opportunities:createdByOthers")
          }
        ]}
        multipleTitle={t("opportunities:byStatus")}
        multipleProp="status"
        defaultMultipleValues={["published", "open"]}
        multipleOptions={[
          {
            value: "published",
            label: t("opportunities:published")
          },
          {
            value: "open",
            label: t("opportunities:open")
          },
          {
            value: "finished",
            label: t("opportunities:finished")
          }
        ]}
      >
        {({ data: opportunities }) => {
          if (opportunitiesLoading && !error) return <SkeletonOpportunities />;

          if (error) {
            return (
              <Placeholder data-test="opportunities-error">
                <Icon type="warning" />
                {t("opportunities:fetchError")}
              </Placeholder>
            );
          }

          if (!opportunities.length)
            return (
              <Placeholder>
                <Icon type="file-search" />
                {t("global:noResults")}
              </Placeholder>
            );

          return (
            <Pagination data={R.reverse(opportunities)} pageSize={PAGE_SIZE}>
              {({ data: opportunities }) => (
                <Grid>
                  {opportunities.map(opportunity => (
                    <OpportunityCard key={opportunity.id} {...opportunity} />
                  ))}
                </Grid>
              )}
            </Pagination>
          );
        }}
      </Filters>
    </div>
  );
}

export default connect(
  R.applySpec({
    company: R.prop("company"),
    idUser: R.path(["user", "id"]),
    opportunities: ({ opportunities: { list, notifications } }, { team }) =>
      list &&
      R.pipe(
        byTeam(team),
        assocNotifications(notifications)
      )(list || []),
    loading: curriedIsLoading,
    error: FETCH_OPPORTUNITIES.getError
  })
)(Opportunities);
